// HTML renderer for the document generator: client quotation (Arabic, RTL,
// A4 portrait, simple or itemized) and supplier RFQ (English, LTR, A4
// landscape). Ported from docs/docgen-handoff/templates, which remain the
// design source of truth — colours, type scale, the InterNum digit trick and
// the wordmark are carried over unchanged.
//
// Shared by the live preview (an iframe srcdoc) and the export worker
// (headless Chromium), so the preview and the PDF come from the same code.
// Pure string building: no React, no DOM, no network.
//
// ⚠️ A quotation is rendered from pricing.js's `clientView` ONLY. This file
// never receives a cost, the agent %, the margin or an FX rate, and must not
// be changed to accept one. render.test.js renders with distinctive internal
// values and fails if any of them reaches the HTML.
//
// Every piece of user text goes through esc(). Images arrive as ready `src`
// strings — signed URLs in the preview, data: URIs in the worker, so the PDF
// embeds them and works offline.

import { CURRENCY_AR, withDefaults } from './content.js';

// ── helpers ─────────────────────────────────────────────────────────────
export function esc(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
const escBr = (v) => esc(v).replace(/\r?\n/g, '<br>');
// A CSS string literal, for @page margin-box content.
const cssStr = (v) => `"${String(v ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ')}"`;

// Latin digits with grouping; decimals only when the value has them.
export function fmtNum(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const whole = Math.round(v * 100) % 100 === 0;
  return v.toLocaleString('en-US', whole
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// 'YYYY-MM-DD' (a Postgres date) → parts, without a timezone shift.
function dateParts(d) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ''));
  return m ? { y: +m[1], mo: +m[2] - 1, d: +m[3] } : null;
}
export const fmtDateAr = (d) => { const p = dateParts(d); return p ? `${p.d} ${MONTHS_AR[p.mo]} ${p.y}` : ''; };
export const fmtDateEn = (d) => { const p = dateParts(d); return p ? `${p.d} ${MONTHS_EN[p.mo]} ${p.y}` : ''; };

const qtyWithUnit = (qty, unit) => [qty === null || qty === undefined ? '' : fmtNum(qty), unit || ''].filter(Boolean).join(' ');

// ── shared CSS ──────────────────────────────────────────────────────────
function fontFaces(base) {
  const f = (family, weight, file, extra = '') =>
    `@font-face{font-family:'${family}';font-weight:${weight};${extra}src:url('${base}fonts/${file}.woff2') format('woff2');}`;
  const digits = 'unicode-range:U+0030-0039,U+002C-002E,U+0025,U+0024,U+00A0;';
  return [
    f('Tajawal', 400, 'tajawal-arabic-400-normal'),
    f('Tajawal', 500, 'tajawal-arabic-500-normal'),
    f('Tajawal', 700, 'tajawal-arabic-700-normal'),
    f('Cormorant Garamond', 400, 'cormorant-garamond-latin-400-normal'),
    f('Cormorant Garamond', 500, 'cormorant-garamond-latin-500-normal'),
    f('Cormorant Garamond', 600, 'cormorant-garamond-latin-600-normal'),
    f('Cormorant Garamond', 700, 'cormorant-garamond-latin-700-normal'),
    f('InterNum', 400, 'inter-latin-400-normal', digits),
    f('InterNum', 600, 'inter-latin-600-normal', digits),
    f('InterNum', 700, 'inter-latin-700-normal', digits),
  ].join('\n');
}

// Photo size (content.photoSize: large | medium | small), chosen per document
// in the form. Large is the handoff templates' size (gallery up to 120 mm,
// thumbnails 24 mm); medium and small step both down together.
export const PHOTO_SIZES = ['large', 'medium', 'small'];
const PHOTO_DIMS = {
  large:  { thumb: 24, col: 28, g1: 120, g2: 62, g3: 44, gg: 52 },
  medium: { thumb: 20, col: 24, g1: 90,  g2: 50, g3: 36, gg: 42 },
  small:  { thumb: 16, col: 20, g1: 62,  g2: 38, g3: 28, gg: 32 },
};
const PHOTO_SIZE_CSS = PHOTO_SIZES.map((k) => {
  const d = PHOTO_DIMS[k];
  return `.ph-${k} td.photo img.thumb{width:${d.thumb}mm;height:${d.thumb}mm;}`
    + `.ph-${k} .w-photo{width:${d.col}mm;}`
    + `.ph-${k} .gallery.g1 img{max-height:${d.g1}mm;}`
    + `.ph-${k} .gallery.g2 img{height:${d.g2}mm;}`
    + `.ph-${k} .gallery.g3 img{height:${d.g3}mm;}`
    + `.ph-${k} .gallery.gg img{height:${d.gg}mm;}`;
}).join('\n');
const photoClass = (size) => `ph-${PHOTO_SIZES.includes(size) ? size : 'large'}`;

// Page colour: WeasyPrint honours @page background; Chromium paints only the
// page box, so html carries the same cream with exact colour printing.
const PAGE_BASE = `
html{background:#F5F3EF;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
body{background:#F5F3EF;}
*{margin:0;padding:0;box-sizing:border-box;}
.num{font-variant-numeric:lining-nums tabular-nums;}
img{max-width:100%;}
.draft-mark{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:50;}
.draft-mark span{transform:rotate(-30deg);font-family:'InterNum','Cormorant Garamond',serif;font-weight:700;
  font-size:88pt;letter-spacing:0.2em;color:rgba(139,115,85,0.13);}
td.photo{background:#FFFFFF;text-align:center;vertical-align:middle;}
td.photo img.thumb{display:block;margin:0.8mm auto;object-fit:contain;
  background:#FFFFFF;border:1px solid #D8D0BE;border-radius:6px;}
td.photo .ref-only{font-size:6.4pt;color:#6B6560;line-height:1.25;margin-top:0.4mm;}
td.photo .dash{color:#D8D0BE;}
.gallery{margin:2mm 0 1mm;}
.gallery .g-item{break-inside:avoid;page-break-inside:avoid;}
.gallery img{display:block;width:100%;object-fit:cover;border-radius:10px;border:1px solid #D8D0BE;background:#FFFFFF;}
.gallery .cap{font-size:8.4pt;color:#6B6560;margin-top:1mm;}
.gallery.grid{display:grid;gap:4mm;}
.gallery.g2{grid-template-columns:1fr 1fr;}
.gallery.g3{grid-template-columns:1fr 1fr 1fr;}
.gallery.gg{grid-template-columns:1fr 1fr;}
${PHOTO_SIZE_CSS}`;

function draftMark(draft) {
  return draft ? '<div class="draft-mark"><span>DRAFT</span></div>' : '';
}

// Line thumbnails: 0–3 images, each optionally "reference only".
function photoCell(imgs, refOnlyText) {
  if (!imgs || !imgs.length) return '<td class="photo"><span class="dash">—</span></td>';
  return `<td class="photo">${imgs.slice(0, 3).map((im) =>
    `<img class="thumb" src="${esc(im.src)}" alt="">${im.reference_only ? `<div class="ref-only">${esc(refOnlyText)}</div>` : ''}`,
  ).join('')}</td>`;
}

function gallery(images, lang) {
  const imgs = images.slice(0, 6);
  if (!imgs.length) return '';
  const cls = imgs.length === 1 ? 'g1' : imgs.length === 2 ? 'grid g2' : imgs.length === 3 ? 'grid g3' : 'grid gg';
  return `<div class="gallery ${cls}">${imgs.map((im) => {
    const cap = lang === 'ar' ? (im.caption_ar || im.caption_en) : (im.caption_en || im.caption_ar);
    return `<div class="g-item"><img src="${esc(im.src)}" alt="">${cap ? `<div class="cap">${escBr(cap)}</div>` : ''}</div>`;
  }).join('')}</div>`;
}

function splitImages(images) {
  const byLine = new Map();
  const galleryImgs = [];
  for (const im of [...(images || [])].sort((a, b) => (a.position || 0) - (b.position || 0))) {
    if (!im.src) continue;
    if (im.line_id) {
      if (!byLine.has(im.line_id)) byLine.set(im.line_id, []);
      byLine.get(im.line_id).push(im);
    } else galleryImgs.push(im);
  }
  return { byLine, galleryImgs };
}

function page(lang, dir, css, body, title, bodyClass) {
  return `<!DOCTYPE html><html lang="${lang}" dir="${dir}"><head><meta charset="utf-8">`
    + `<title>${esc(title)}</title><style>${css}</style></head><body class="${bodyClass}">${body}</body></html>`;
}

// ═════════════════════════════════════════════════════════════════════════
// Client quotation (Arabic, RTL)
// ═════════════════════════════════════════════════════════════════════════
const QT_CSS = `
@page{size:A4;margin:12mm 14mm 16mm 14mm;background:#F5F3EF;}
@media screen{body{width:210mm;margin:0 auto;padding:12mm 14mm 16mm;}}
body{font-family:'InterNum','Tajawal',sans-serif;color:rgba(0,0,0,0.88);font-size:10pt;line-height:1.62;}
.masthead{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #1A1814;padding-bottom:5mm;margin-bottom:6mm;}
.masthead img.logo{width:46mm;display:block;}
.masthead .tag{font-size:8pt;color:#6B6560;letter-spacing:0.1em;margin-top:2mm;font-family:'Cormorant Garamond',serif;}
.doc-id{text-align:left;}
.doc-id h1{font-weight:700;font-size:17pt;color:#1A1814;}
.doc-id .meta{margin-top:1.5mm;font-size:9pt;color:#3D3A35;}
.doc-id .meta b{color:#1A1814;}
.doc-id .rev{color:#8B7355;}
.client{background:#FAF9F7;border:1px solid #E8E5DE;border-radius:10px;padding:4mm 5mm;margin-bottom:5mm;display:flex;justify-content:space-between;gap:6mm;font-size:9.5pt;}
.client b{color:#1A1814;}
h2.sec{font-weight:700;font-size:12pt;color:#1A1814;margin:5mm 0 2mm;border-right:3px solid #8B7355;padding-right:3mm;break-after:avoid;}
p.lead{font-size:9.5pt;margin-bottom:2mm;color:#3D3A35;}
table{width:100%;border-collapse:collapse;background:#FAF9F7;}
th{background:#1A1814;color:#F5F3EF;font-weight:500;font-size:9pt;padding:2.2mm 3mm;text-align:right;border:1px solid #1A1814;}
td{border:1px solid #E8E5DE;padding:2.2mm 3mm;font-size:9.5pt;vertical-align:top;}
tr{break-inside:avoid;page-break-inside:avoid;}
thead{display:table-header-group;}
.c{text-align:center;}
td.money{font-weight:700;white-space:nowrap;}
td .desc{display:block;color:#6B6560;font-size:8.6pt;margin-top:0.6mm;}
tr.sec-row td{background:#EDE8DC;font-weight:700;color:#1A1814;}
.total-row td{background:#EDE8DC;font-weight:700;font-size:11pt;color:#1A1814;border-color:#D8D0BE;}
.badge{display:inline-block;background:#8B7355;color:#F5F3EF;font-size:7.8pt;font-weight:700;border-radius:999px;padding:1mm 3mm;margin-inline-start:2mm;}
.muted{color:#6B6560;font-size:8.8pt;margin-top:1.5mm;}
table.sys{margin-bottom:3.5mm;}
tr.sys-title th{font-weight:700;font-size:10pt;}
tr.sys-title th.st{text-align:left;font-weight:400;font-size:9pt;width:34mm;white-space:nowrap;}
tr.sys-title th.st b{color:#F5F3EF;font-size:10.5pt;}
tr.sys-title .no{display:inline-block;background:#8B7355;color:#F5F3EF;border-radius:999px;width:6mm;height:6mm;line-height:6mm;text-align:center;margin-left:2.5mm;font-size:9pt;}
tr.cols th{background:#EDE8DC;color:#3D3A35;font-weight:700;font-size:8.5pt;padding:1.6mm 3mm;border:1px solid #E8E5DE;}
td.sub{font-weight:700;color:#1A1814;}
td.spec{color:rgba(0,0,0,0.78);}
.twocol{display:flex;gap:5mm;margin-top:2mm;break-inside:avoid;page-break-inside:avoid;}
.twocol .box{flex:1;background:#FAF9F7;border:1px solid #E8E5DE;border-radius:10px;padding:3.5mm 4.5mm;}
.box h3{font-size:10.5pt;font-weight:700;color:#1A1814;margin-bottom:1.5mm;}
.box ul{padding-right:4.5mm;}
.box li{font-size:9pt;margin-bottom:0.8mm;}
.box.inc h3{color:#2D7A4F;}
.box.exc h3{color:#8B7355;}
table.terms td.lab{width:32%;background:#EDE8DC;font-weight:700;font-size:9pt;color:#1A1814;}
.note{background:#EDE8DC;border-radius:10px;padding:3.5mm 4.5mm;margin-top:4mm;font-size:9pt;color:#3D3A35;break-inside:avoid;page-break-inside:avoid;}
.note b{color:#1A1814;}
.note.notice{background:#F6EAD9;border:1px solid #D8B98A;}
`;

function totalLabelAr(content, doc) {
  if (content.totalLabel) return content.totalLabel;
  const term = [doc.incoterm, doc.port].filter(Boolean).join(' ');
  return term ? `الإجمالي — توريد المواد (${term})` : 'الإجمالي';
}

function depositText(dep, cur) {
  let t = `${fmtNum(dep.pct)}% = <b>${fmtNum(dep.amount)}</b> ${cur}`;
  if (dep.paid > 0) {
    t += `، يُخصم منها المبلغ المدفوع سابقاً ${fmtNum(dep.paid)} ${cur}، والمتبقي <b>${fmtNum(dep.remaining)}</b> ${cur}`;
  }
  return t;
}

/**
 * @param {object} p
 * @param {object} p.doc         doc_documents row (ref_no, revision, issued_on, valid_until, incoterm, port, layout)
 * @param {object} p.clientView  from computeQuotation — never the internal view
 * @param {object[]} [p.images]  doc_images rows with a resolved `src`
 * @param {string} [p.assetBase] prefix for fonts/ and the logo, ending in '/'
 * @param {string} [p.today]     'YYYY-MM-DD' shown as the date on a draft
 */
export function quotationHtml({ doc, clientView, images = [], assetBase = '/docgen/', today = '' }) {
  const c = withDefaults('quotation', doc.content);
  const cv = clientView;
  const cur = CURRENCY_AR[cv.currency] || cv.currency;
  const draft = !doc.ref_no;
  const ref = draft ? 'DRAFT' : doc.ref_no;
  const isRevision = (doc.revision || 0) > 0 || !!doc.parent_id;
  const date = doc.issued_on || today;
  const { byLine, galleryImgs } = splitImages(images);
  const refOnly = 'مرجعية فقط — ليست المنتج المطابق';

  const css = fontFaces(assetBase) + PAGE_BASE + QT_CSS
    + `@page{@bottom-right{content:"MAABAR — maabar.io";font-family:'Cormorant Garamond',serif;font-size:7.5pt;color:#6B6560;}`
    + `@bottom-left{content:${cssStr(`عرض سعر ${ref} — صفحة `)} counter(page) " من " counter(pages);font-family:'InterNum','Tajawal';font-size:7.5pt;color:#6B6560;}}`;

  let h = draftMark(draft);

  // 1. masthead
  h += `<div class="masthead"><div><img class="logo" src="${esc(assetBase)}maabar-wordmark.svg" alt="MAABAR">`
    + '<div class="tag">B2B SOURCING · SAUDI ARABIA — CHINA</div></div>'
    + '<div class="doc-id"><h1>عرض سعر</h1><div class="meta">'
    + `<div>رقم المرجع: <b class="num">${esc(ref)}</b></div>`
    + `<div>التاريخ: <b>${esc(fmtDateAr(date))}</b>${isRevision ? ' <span class="rev">(نسخة محدثة)</span>' : ''}</div>`
    + (doc.valid_until ? `<div>صالح حتى: <b>${esc(fmtDateAr(doc.valid_until))}</b></div>` : '')
    + '</div></div></div>';

  // 2. client box
  h += `<div class="client"><div>مقدم إلى: <b>${esc(c.clientName)}</b></div>`
    + (c.subject ? `<div>الموضوع: <b>${esc(c.subject)}</b></div>` : '') + '</div>';

  // 3. scope
  if (c.scopeIntro || c.scopeRows.length) {
    h += `<h2 class="sec">${esc(c.scopeTitle)}</h2>`;
    if (c.scopeIntro || c.scopeBadge) {
      h += `<p class="lead">${escBr(c.scopeIntro)}${c.scopeBadge ? ` <span class="badge">${esc(c.scopeBadge)}</span>` : ''}</p>`;
    }
    if (c.scopeRows.length) {
      h += '<table><thead><tr><th class="c" style="width:8%">م</th><th style="width:22%">البند</th><th>التفاصيل</th></tr></thead><tbody>'
        + c.scopeRows.map((r, i) => `<tr><td class="c num">${i + 1}</td><td><b>${esc(r.label)}</b></td><td>${escBr(r.details)}</td></tr>`).join('')
        + '</tbody></table>';
    }
  }

  // 4. pricing
  if (cv.layout === 'itemized') {
    if (c.itemizedIntro) h += `<p class="lead" style="margin-top:4mm">${escBr(c.itemizedIntro)}</p>`;
    cv.sections.forEach((s, i) => {
      const hasPhoto = s.lines.some((l) => byLine.has(l.id));
      const cols = hasPhoto ? 4 : 3;
      h += '<table class="sys"><thead>'
        + `<tr class="sys-title"><th colspan="${cols - 1}"><span class="no num">${i + 1}</span>${esc(s.title)}</th>`
        + `<th class="st">${esc(c.perUnitLabel)}: <b class="num">${fmtNum(s.subtotal)}</b> ${esc(cur)}</th></tr>`
        + '<tr class="cols">' + (hasPhoto ? '<th class="c w-photo">صورة</th>' : '')
        + '<th style="width:32%">البند</th><th>المواصفات</th><th class="c" style="width:14%">الكمية</th></tr>'
        + '</thead><tbody>'
        + s.lines.map((l) => '<tr>' + (hasPhoto ? photoCell(byLine.get(l.id), refOnly) : '')
          + `<td class="sub">${esc(l.item)}</td><td class="spec">${escBr(l.description)}</td>`
          + `<td class="c num">${esc(qtyWithUnit(l.qty, l.unit))}</td></tr>`).join('')
        + '</tbody></table>';
    });
    const sm = cv.summary || { units: null, unitPrice: null, total: cv.total };
    h += `<h2 class="sec">${esc(c.summaryTitle)}</h2>`
      + '<table><thead><tr><th>البند</th><th class="c" style="width:12%">الكمية</th>'
      + `<th class="c" style="width:22%">${esc(c.unitLabel)} (${esc(cur)})</th><th class="c" style="width:22%">الإجمالي (${esc(cur)})</th></tr></thead><tbody>`
      + `<tr><td>${esc(c.summaryItem)}</td><td class="c num">${fmtNum(sm.units)}</td>`
      + `<td class="c money num">${fmtNum(sm.unitPrice)}</td><td class="c money num">${fmtNum(sm.total)}</td></tr>`
      + `<tr class="total-row"><td colspan="3">${esc(totalLabelAr(c, doc))}</td><td class="c num">${fmtNum(cv.total)} ${esc(cur)}</td></tr>`
      + '</tbody></table>';
  } else {
    const all = cv.sections.flatMap((s) => s.lines);
    const hasPhoto = all.some((l) => byLine.has(l.id));
    const multi = cv.sections.length > 1;
    const width = hasPhoto ? 5 : 4;
    h += `<h2 class="sec">${esc(c.pricingTitle)}</h2>`
      + '<table><thead><tr>' + (hasPhoto ? '<th class="c w-photo">صورة</th>' : '')
      + `<th>البند</th><th class="c" style="width:12%">الكمية</th><th class="c" style="width:20%">${esc(c.unitLabel)} (${esc(cur)})</th>`
      + `<th class="c" style="width:20%">الإجمالي (${esc(cur)})</th></tr></thead><tbody>`;
    for (const s of cv.sections) {
      if (multi && s.title) h += `<tr class="sec-row"><td colspan="${width}">${esc(s.title)}</td></tr>`;
      for (const l of s.lines) {
        h += '<tr>' + (hasPhoto ? photoCell(byLine.get(l.id), refOnly) : '')
          + `<td>${esc(l.item)}${l.description ? `<span class="desc">${escBr(l.description)}</span>` : ''}</td>`
          + `<td class="c num">${esc(qtyWithUnit(l.qty, l.unit))}</td>`
          + `<td class="c money num">${fmtNum(l.unitPrice)}</td><td class="c money num">${fmtNum(l.total)}</td></tr>`;
      }
    }
    h += `<tr class="total-row"><td colspan="${width - 1}">${esc(totalLabelAr(c, doc))}</td><td class="c num">${fmtNum(cv.total)} ${esc(cur)}</td></tr>`
      + '</tbody></table>';
  }
  if (c.showVat && c.vatText) h += `<p class="muted">${escBr(c.vatText)}</p>`;

  // 5. gallery
  if (galleryImgs.length) {
    if (c.galleryTitle) h += `<h2 class="sec">${esc(c.galleryTitle)}</h2>`;
    h += gallery(galleryImgs, 'ar');
  }

  // 6. includes / excludes
  const inc = c.includes.filter(Boolean);
  const exc = c.excludes.filter(Boolean);
  if (inc.length || exc.length) {
    h += '<div class="twocol">'
      + (inc.length ? `<div class="box inc"><h3>يشمل العرض</h3><ul>${inc.map((x) => `<li>${escBr(x)}</li>`).join('')}</ul></div>` : '')
      + (exc.length ? `<div class="box exc"><h3>لا يشمل العرض</h3><ul>${exc.map((x) => `<li>${escBr(x)}</li>`).join('')}</ul></div>` : '')
      + '</div>';
  }

  // 7. terms
  const rows = c.terms.map((t) => {
    if (t.kind === 'deposit') return [t.label, depositText(cv.deposit, esc(cur))];
    if (t.kind === 'validity') return doc.valid_until ? [t.label, `حتى ${esc(fmtDateAr(doc.valid_until))}`] : null;
    return t.value ? [t.label, escBr(t.value)] : null;
  }).filter(Boolean);
  if (rows.length) {
    h += '<h2 class="sec">الشروط</h2><table class="terms">'
      + rows.map(([l, v]) => `<tr><td class="lab">${esc(l)}</td><td>${v}</td></tr>`).join('') + '</table>';
  }

  // 8–9. notice and note. No signature or approval blocks on any document
  // (owner, 2026-09-28) — which also retires the "signatures alone on the
  // last page" problem the handoff sample had.
  if (c.notice && c.notice.body) {
    h += `<div class="note notice">${c.notice.title ? `<b>${esc(c.notice.title)}:</b> ` : ''}${escBr(c.notice.body)}</div>`;
  }
  if (c.note && c.note.body) {
    h += `<div class="note">${c.note.title ? `<b>${esc(c.note.title)}:</b> ` : ''}${escBr(c.note.body)}</div>`;
  }

  return page('ar', 'rtl', css, h, `عرض سعر ${ref}`, photoClass(c.photoSize));
}

// ═════════════════════════════════════════════════════════════════════════
// Supplier RFQ (English, LTR, landscape)
// ═════════════════════════════════════════════════════════════════════════
const RFQ_CSS = `
@page{size:A4 landscape;margin:11mm 12mm 14mm 12mm;background:#F5F3EF;}
@media screen{body{width:297mm;margin:0 auto;padding:11mm 12mm 14mm;}}
body{font-family:'InterNum','Cormorant Garamond',Georgia,serif;color:rgba(0,0,0,0.88);font-size:9.5pt;line-height:1.45;}
.ar{font-family:'InterNum','Tajawal',sans-serif;direction:rtl;}
.masthead{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #1A1814;padding-bottom:5mm;margin-bottom:5mm;}
.masthead img.logo{width:52mm;display:block;}
.masthead .tag{font-size:8.5pt;color:#6B6560;letter-spacing:0.16em;text-transform:uppercase;margin-top:2.2mm;}
.doc-id{text-align:right;}
.doc-id h1{font-family:'Cormorant Garamond',serif;font-weight:600;font-size:19pt;color:#1A1814;line-height:1.1;}
.doc-id .ar-title{font-family:'Tajawal',sans-serif;font-weight:500;font-size:10pt;color:#8B7355;margin-top:1mm;}
.meta{margin-top:2.5mm;font-size:8.5pt;color:#3D3A35;}
.meta b{color:#1A1814;}
.meta span{margin-left:6mm;}
.intro{display:flex;gap:6mm;margin-bottom:6mm;}
.intro .box{background:#FAF9F7;border:1px solid #E8E5DE;border-radius:10px;padding:4mm 5mm;flex:1;}
.intro h3{font-family:'Cormorant Garamond',serif;font-weight:700;font-size:10.5pt;color:#1A1814;letter-spacing:0.04em;margin-bottom:1.6mm;}
.intro p,.intro li{font-size:8.8pt;color:#3D3A35;}
.intro ol{padding-left:4.5mm;}
.intro li{margin-bottom:0.8mm;}
.sport{margin-bottom:6mm;}
.sport-head{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:1.8mm;break-after:avoid;}
.sport-head h2{font-family:'Cormorant Garamond',serif;font-weight:700;font-size:14pt;color:#1A1814;}
.sport-head .count{font-size:8.5pt;color:#6B6560;}
.sport-head .count b{color:#8B7355;font-size:10pt;}
table{width:100%;border-collapse:collapse;background:#FAF9F7;}
thead{display:table-header-group;}
th{background:#1A1814;color:#F5F3EF;font-family:'Cormorant Garamond',serif;font-weight:600;font-size:8.3pt;letter-spacing:0.05em;padding:2mm;text-align:left;border:1px solid #1A1814;}
th.sup{background:#8B7355;border-color:#8B7355;}
td{border:1px solid #E8E5DE;padding:1.7mm 2mm;font-size:8.8pt;vertical-align:middle;}
td.item{font-weight:600;font-family:'Cormorant Garamond',serif;font-size:9.2pt;}
td.desc{color:rgba(0,0,0,0.82);}
td.brand{color:#3D3A35;}
.c{text-align:center;}
td.qty{font-weight:700;font-size:9.5pt;}
td.fill{background:#FFFFFF;border:1px solid #D8D0BE;}
tr{break-inside:avoid;page-break-inside:avoid;}
.w-no{width:4%}.w-item{width:11%}.w-desc{width:24%}.w-brand{width:9%}
.w-unit{width:6%}.w-qty{width:5%}.w-price{width:8%}.w-total{width:8%}.w-lead{width:11%}.w-rem{width:12%}
.grand{margin:2mm 0 7mm;background:#EDE8DC;border:1px solid #D8D0BE;border-radius:10px;padding:3.4mm 5mm;display:flex;justify-content:space-between;align-items:baseline;}
.grand .lbl{font-family:'Cormorant Garamond',serif;font-weight:700;font-size:11.5pt;color:#1A1814;}
.grand .val{font-weight:700;font-size:13pt;color:#1A1814;}
.supplier{break-inside:avoid;page-break-inside:avoid;}
.supplier h2{font-family:'Cormorant Garamond',serif;font-weight:700;font-size:14.5pt;color:#1A1814;margin-bottom:1mm;}
.supplier .sub{font-size:8.8pt;color:#6B6560;margin-bottom:2.5mm;}
table.terms td.lab{width:30%;background:#EDE8DC;font-family:'Cormorant Garamond',serif;font-weight:600;font-size:9.5pt;color:#1A1814;}
table.terms td.in{background:#FFFFFF;border:1px solid #D8D0BE;height:8mm;}
table.terms td.hint{width:28%;color:#B0AB9E;font-size:8pt;background:#FAF9F7;}
`;

/**
 * The RFQ carries NO price of ours and no client identity: items,
 * descriptions, brands, units and quantities only; the bronze columns are
 * empty for the supplier. It takes raw sections/lines but reads only the
 * whitelisted text fields below.
 */
export function rfqHtml({ doc, sections, lines, images = [], assetBase = '/docgen/', today = '' }) {
  const c = withDefaults('rfq', doc.content);
  const draft = !doc.ref_no;
  const ref = draft ? 'DRAFT' : doc.ref_no;
  const date = doc.issued_on || today;
  const { byLine, galleryImgs } = splitImages(images);
  const byPos = (a, b) => (a.position || 0) - (b.position || 0);
  const secs = [...(sections || [])].sort(byPos).map((s) => ({
    id: s.id,
    title: s.title || '',
    lines: (lines || []).filter((l) => l.section_id === s.id).sort(byPos).map((l) => ({
      id: l.id, item: l.item || '', description: l.description || '', brand: l.brand || '',
      unit: l.unit || '', qty: l.qty === null || l.qty === undefined ? null : Number(l.qty),
    })),
  }));
  const sumQty = (ls) => ls.reduce((a, l) => a + (Number.isFinite(l.qty) ? l.qty : 0), 0);
  const grand = secs.reduce((a, s) => a + sumQty(s.lines), 0);

  const css = fontFaces(assetBase) + PAGE_BASE + RFQ_CSS
    + `@page{@bottom-left{content:${cssStr(`MAABAR — Order Request ${ref}`)};font-family:'InterNum','Cormorant Garamond',serif;font-size:7.5pt;color:#6B6560;}`
    + `@bottom-right{content:"Page " counter(page) " of " counter(pages);font-family:'InterNum','Cormorant Garamond',serif;font-size:7.5pt;color:#6B6560;}}`;

  let h = draftMark(draft);
  h += `<div class="masthead"><div><img class="logo" src="${esc(assetBase)}maabar-wordmark.svg" alt="MAABAR">`
    + '<div class="tag">B2B Sourcing &nbsp;·&nbsp; Saudi Arabia — China</div></div>'
    + `<div class="doc-id"><h1>${esc(c.title)}</h1>`
    + (c.arSubtitle ? `<div class="ar-title" dir="rtl">${esc(c.arSubtitle)}</div>` : '')
    + `<div class="meta"><span>Ref: <b class="num">${esc(ref)}</b></span>`
    + `<span>Date: <b>${esc(fmtDateEn(date))}</b></span><span>Prepared by: <b>MAABAR — maabar.io</b></span></div></div></div>`;

  h += '<div class="intro">'
    + (c.scopeText ? `<div class="box"><h3>Scope</h3><p>${escBr(c.scopeText)}</p></div>` : '')
    + (c.instructions.filter(Boolean).length
      ? `<div class="box"><h3>Instructions to Supplier</h3><ol>${c.instructions.filter(Boolean).map((x) => `<li>${escBr(x)}</li>`).join('')}</ol></div>`
      : '')
    + '</div>';

  for (const s of secs) {
    const hasPhoto = s.lines.some((l) => byLine.has(l.id));
    h += '<section class="sport"><div class="sport-head">'
      + `<h2>${esc(s.title)}</h2><span class="count">Total items&nbsp;&nbsp;<b class="num">${fmtNum(sumQty(s.lines))}</b></span></div>`
      + '<table><thead><tr><th class="c w-no">No.</th>'
      + (hasPhoto ? '<th class="c w-photo">Photo / Reference</th>' : '')
      + '<th class="w-item">Item</th><th class="w-desc">Description</th><th class="w-brand">Brand</th>'
      + '<th class="c w-unit">Unit</th><th class="c w-qty">Qty</th>'
      + '<th class="c w-price sup">Unit Price (USD)</th><th class="c w-total sup">Total (USD)</th>'
      + '<th class="c w-lead sup">Availability / Lead Time</th><th class="w-rem sup">Remarks</th></tr></thead><tbody>'
      + s.lines.map((l, i) => `<tr><td class="c num">${i + 1}</td>`
        + (hasPhoto ? photoCell(byLine.get(l.id), 'Reference only — not exact product') : '')
        + `<td class="item">${esc(l.item)}</td><td class="desc">${escBr(l.description)}</td>`
        + `<td class="brand">${esc(l.brand)}</td><td class="c">${esc(l.unit)}</td>`
        + `<td class="c num qty">${fmtNum(l.qty)}</td>`
        + '<td class="fill"></td><td class="fill"></td><td class="fill"></td><td class="fill"></td></tr>').join('')
      + '</tbody></table></section>';
  }

  if (galleryImgs.length) {
    if (c.galleryTitle) h += `<div class="sport-head" style="margin-top:2mm"><h2>${esc(c.galleryTitle)}</h2></div>`;
    h += gallery(galleryImgs, 'en');
  }

  h += `<div class="grand"><span class="lbl">Grand total — all sections</span><span class="val num">${fmtNum(grand)}&nbsp;&nbsp;items</span></div>`;

  h += '<section class="supplier"><h2>Commercial Terms — to be completed by the supplier</h2>'
    + '<div class="sub">All fields below are required for the quotation to be considered complete.</div><table class="terms">'
    + c.termsRows.map((r) => `<tr><td class="lab">${esc(r.label)}</td><td class="in"></td><td class="hint">${esc(r.hint)}</td></tr>`).join('')
    + '</table></section>';

  return page('en', 'ltr', css, h, `Order Request ${ref}`, photoClass(c.photoSize));
}

// File name for the stored / downloaded PDF.
export function pdfFileName(doc) {
  const ref = doc.ref_no || 'DRAFT';
  return doc.kind === 'rfq' ? `MAABAR-Order-Request-${ref}.pdf` : `MAABAR-Quotation-${ref}.pdf`;
}
